
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthReadiness } from '@/hooks/useAuthReadiness';
import { Order } from '@/components/dashboard/OrderHistory';

export const useOrderHistory = () => {
  const { user, canQueryUserData } = useAuthReadiness();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  const { data: orders, isLoading, refetch } = useQuery({
    queryKey: ['user-orders', user?.id, searchTerm, statusFilter, dateFilter],
    queryFn: async () => {
      if (!user?.id) return [];
      
      let query = supabase
        .from('orders')
        .select(`
          *,
          order_items (
            quantity,
            products (
              image_url
            )
          )
        `)
        .eq('user_id', user.id);
      
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      if (searchTerm) {
        query = query.ilike('order_number', `%${searchTerm}%`);
      }

      if (dateFilter !== 'all') {
        const days = parseInt(dateFilter, 10);
        const fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - days);
        query = query.gte('created_at', fromDate.toISOString());
      }

      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching orders:', error);
        throw error;
      }
      
      return data as Order[];
    },
    enabled: canQueryUserData,
  });

  const hasFilters = !!searchTerm || statusFilter !== 'all' || dateFilter !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateFilter('all');
  };

  return {
    orders,
    isLoading,
    refetch,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    dateFilter,
    setDateFilter,
    hasFilters,
    clearFilters,
  };
};
